import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Platform, StyleSheet, Text, View } from 'react-native';
import Orientation from 'react-native-orientation-locker';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WebView, type WebViewMessageEvent } from 'react-native-webview';
import { useDispatch, useSelector } from 'react-redux';

import {
	arcadeAnalyticsSessionId,
	arcadeAppVersion,
	isArcadeHelixEnabled,
	trackArcadeUiEvent,
} from '../../../services/arcadeAnalytics';
import type { ArcadeGameType } from '../../../services/arcadeApi';
import { ARCADE_GAME_PACK_DIRS } from '../../../services/arcadeApi';
import { RootState } from '../../../store';
import { profileRequest } from '../../../store/auth/authActions';
import { requestNewAccessToken } from '../../../utils/axiosInstance';
import { env } from '../../../utils/env';
import { resolveArcadePack } from '../../../utils/gamePack';
import {
	refreshVipTicketBalance,
	setVipTicketBalance,
	useVipTicketPlayerId,
} from '../../../utils/hooks/useVipTicketBalance';
import type { RootStackParamList } from '../../navigator';
import { TouchableOpacity } from '../common/Touchable';
import BecomeVipModal from '../Modals/BecomeVipModal';
import NotEnoughChipsModal from '../Modals/NotEnoughChipsModal';
import {
	ARCADE_NATIVE_PRELUDE,
	arcadeEnvelope,
	type ArcadeGameToHostMessage,
	type ArcadeHostToGameMessage,
	type ArcadeInitData,
	nativeDeliverScript,
	parseArcadeMessage,
} from './arcadeBridge';
import ArcadeLoadingOverlay from './ArcadeLoadingOverlay';

interface ArcadeGameModalProps {
	gameType: ArcadeGameType;
	/** Called once the game is closed (user, host or fatal error); the profile is refreshed first. */
	onClose: VoidFunction;
}

const webIframeStyle: CSSProperties = { width: '100%', height: '100%', border: 'none' };
/** iOS modal orientation; Android and the device lock are handled by the orientation locker. */
const ARCADE_MODAL_ORIENTATIONS: Array<'portrait'> = ['portrait'];

/**
 * Hosts one Svara VIP Arcade game pack (Pixi 8) in a WebView (native) or iframe (web).
 * Self-contained: whichever screen opens a game (the redesign's VIP / Bonuses entry, or
 * the temporary sidebar entry) renders `<ArcadeGameModal gameType=... onClose=... />` and
 * nothing else; the "become VIP" and "not enough chips" flows are handled here.
 *
 * Unlike the legacy mini-game host nothing travels in the URL: the game announces
 * itself with ARCADE_READY and receives token, API URL, language and translations in
 * ARCADE_INIT over the bridge (see arcadeBridge.ts). Balance changes the game reports
 * are used to refresh the profile, never trusted as the wallet itself.
 */
export default function ArcadeGameModal({ gameType, onClose }: ArcadeGameModalProps) {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const dispatch = useDispatch();
	const [showBecomeVip, setShowBecomeVip] = useState(false);
	const [showNotEnoughChips, setShowNotEnoughChips] = useState(false);
	const auth = useSelector((state: RootState) => state.auth);
	const ticketPlayerId = useVipTicketPlayerId();
	const langCode = useSelector((state: RootState) => state.i18n.langCode);
	const i18nBundle = useSelector((state: RootState) => state.i18n.bundle);
	const soundEnabled = useSelector(
		(state: RootState) =>
			(state as unknown as { settings?: { soundEnabled?: boolean } }).settings?.soundEnabled ??
			true,
	);
	const webViewRef = useRef<WebView>(null);
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const [ready, setReady] = useState(false);
	// The game's first scene is on screen; until then the loading overlay covers the WebView.
	const [shown, setShown] = useState(false);
	const platform = Platform.OS;
	// The modal is drawn under the status and navigation bars; the game itself keeps clear
	// of the camera cutout and the gesture bar the way every screen of the app does, by
	// sitting inside the safe area on a black frame (the games fade to black at both ends).
	const insets = useSafeAreaInsets();

	const pack = useMemo(
		() =>
			platform === 'web'
				? {
						source: 'web' as const,
						baseUrl: `${window.location.origin}/arcade`,
						readAccessUrl: undefined as string | undefined,
					}
				: resolveArcadePack(),
		[platform],
	);
	const packDir = ARCADE_GAME_PACK_DIRS[gameType];
	const gameUrl = packDir ? `${pack.baseUrl}/${packDir}/index.html` : null;

	const buildInitData = useCallback(
		(token: string): ArcadeInitData => ({
			token,
			apiUrl: `${env('SVARA_API_URL')!.replace(/\/+$/, '')}/api`,
			casinoId: env('CASINO_ID') ?? '',
			playerId: String(auth.user?.id ?? ''),
			language: langCode || 'en',
			translations: i18nBundle || {},
			soundEnabled,
			platform: platform === 'web' ? 'web' : platform === 'ios' ? 'ios' : 'android',
			appVersion: arcadeAppVersion(),
			analytics: {
				deltaDna: true,
				helix: isArcadeHelixEnabled(),
				...(arcadeAnalyticsSessionId() ? { sessionId: arcadeAnalyticsSessionId()! } : {}),
			},
		}),
		[auth.user?.id, i18nBundle, langCode, platform, soundEnabled],
	);

	const sendToGame = useCallback(
		(message: ArcadeHostToGameMessage) => {
			if (platform === 'web') {
				iframeRef.current?.contentWindow?.postMessage(arcadeEnvelope(message), '*');
				return;
			}
			webViewRef.current?.injectJavaScript(nativeDeliverScript(message));
		},
		[platform],
	);

	const handleGameMessage = useCallback(
		async (message: ArcadeGameToHostMessage) => {
			switch (message.type) {
				case 'ARCADE_READY':
					if (!auth.token) return;
					sendToGame({ type: 'ARCADE_INIT', payload: buildInitData(auth.token) });
					setReady(true);
					break;
				case 'ARCADE_CLOSE':
					dispatch(profileRequest());
					onClose();
					break;
				case 'ARCADE_SHOWN':
					setShown(true);
					break;
				case 'ARCADE_BALANCE':
					// The backend is the wallet; the game's number only tells us to re-read it.
					dispatch(profileRequest());
					// Tickets go further: the count is the round receipt's, shown at once so the
					// VIP Arcade badge and gate behind this modal never offer a ticket just spent,
					// and the wallet read confirms it.
					if (ticketPlayerId) {
						setVipTicketBalance(ticketPlayerId, message.payload.tickets);
						refreshVipTicketBalance(ticketPlayerId);
					}
					// Older packs never say ARCADE_SHOWN; their first balance means the scene is up.
					setShown(true);
					break;
				case 'ARCADE_NOT_ENOUGH_CHIPS':
					setShowNotEnoughChips(true);
					break;
				case 'ARCADE_OPEN_STORE':
					// The "+" next to the chips balance: straight to the chip packages.
					navigation.navigate('BuyPackages', { activeTab: 'chips' });
					break;
				case 'ARCADE_BECOME_VIP':
					setShowBecomeVip(true);
					break;
				case 'ARCADE_TOKEN_EXPIRED': {
					try {
						const token = await requestNewAccessToken();
						sendToGame({ type: 'ARCADE_TOKEN', payload: { token } });
					} catch (error) {
						if (__DEV__) console.error('[arcade] token refresh failed', error);
					}
					break;
				}
				case 'ARCADE_UI_EVENT':
					trackArcadeUiEvent(message.payload.name, message.payload.params);
					break;
				case 'ARCADE_ERROR':
					if (__DEV__) console.error('[arcade] game error', message.payload);
					if (message.payload.fatal) onClose();
					break;
				default:
					break;
			}
		},
		[auth.token, buildInitData, dispatch, onClose, sendToGame, ticketPlayerId],
	);

	const onBuyChips = () => {
		setShowNotEnoughChips(false);
		onClose();
		navigation.navigate('BuyPackages', { activeTab: 'chips' });
	};

	// Web: the iframe posts to window.parent.
	useEffect(() => {
		if (platform !== 'web') return undefined;
		const listener = (event: MessageEvent) => {
			if (iframeRef.current && event.source !== iframeRef.current.contentWindow) return;
			const message = parseArcadeMessage(event.data);
			if (message) void handleGameMessage(message);
		};
		window.addEventListener('message', listener);
		return () => window.removeEventListener('message', listener);
	}, [handleGameMessage, platform]);

	// The arcade games are portrait only. Lock the device while a game is open and hand
	// the orientation back to the app (and its own rules) when it closes.
	useEffect(() => {
		if (platform === 'web') return undefined;
		Orientation.lockToPortrait();
		return () => Orientation.unlockAllOrientations();
	}, [platform]);

	// The game is a full-screen Modal above the navigator, so anything that navigates the
	// host screen away (Become VIP -> BuyPackages, a notification, a deep link) would open
	// underneath it, invisible. Closing on blur keeps the navigator authoritative.
	useEffect(() => navigation.addListener('blur', onClose), [navigation, onClose]);

	// The screen behind keeps its focus while the game is open, so its focus refresh never runs
	// on the way back: re-read the ticket wallet as the game closes, however it closes.
	useEffect(
		() => () => {
			if (ticketPlayerId) refreshVipTicketBalance(ticketPlayerId);
		},
		[ticketPlayerId],
	);

	// Token refreshed elsewhere in the app: keep the game in sync.
	useEffect(() => {
		if (ready && auth.token) sendToGame({ type: 'ARCADE_TOKEN', payload: { token: auth.token } });
	}, [auth.token, ready, sendToGame]);

	useEffect(() => {
		const webView = webViewRef.current;
		return () => {
			webView?.injectJavaScript(nativeDeliverScript({ type: 'ARCADE_DESTROY', payload: {} }));
			webView?.stopLoading();
		};
	}, []);

	const onWebViewMessage = (event: WebViewMessageEvent) => {
		const raw = event.nativeEvent.data;
		const message = parseArcadeMessage(raw);
		if (message) {
			void handleGameMessage(message);
			return;
		}
		if (!__DEV__) return;
		try {
			const debug = JSON.parse(raw) as { type: string; data: unknown };
			if (debug.type === 'console.error' || debug.type === 'error') {
				console.error('[arcade game]', debug.data);
			}
		} catch {
			console.log('[arcade game raw]', raw);
		}
	};

	if (!auth.token || !gameUrl) {
		return (
			<Modal
				animationType='none'
				transparent
				visible
				presentationStyle='overFullScreen'
				supportedOrientations={ARCADE_MODAL_ORIENTATIONS}
				statusBarTranslucent
				navigationBarTranslucent
				onRequestClose={onClose}
			>
				<View style={styles.overlay}>
					<View style={styles.errorContainer}>
						<Text style={styles.errorText}>
							{!auth.token ? 'No authentication token found' : `No game pack for ${gameType}`}
						</Text>
						<TouchableOpacity onPress={onClose} style={styles.errorButton}>
							<Text style={styles.errorButtonText}>Close</Text>
						</TouchableOpacity>
					</View>
				</View>
			</Modal>
		);
	}

	return (
		<>
			<Modal
				animationType='fade'
				transparent
				visible
				presentationStyle='overFullScreen'
				supportedOrientations={ARCADE_MODAL_ORIENTATIONS}
				statusBarTranslucent
				navigationBarTranslucent
				onRequestClose={onClose}
			>
				<View style={styles.overlay}>
					<View
						style={[
							styles.container,
							{
								paddingTop: insets.top,
								paddingBottom: insets.bottom,
								paddingLeft: insets.left,
								paddingRight: insets.right,
							},
						]}
					>
						{platform !== 'web' ? (
							<WebView
								ref={webViewRef}
								source={{ uri: gameUrl }}
								style={styles.webview}
								javaScriptEnabled
								domStorageEnabled
								allowsInlineMediaPlayback
								mediaPlaybackRequiresUserAction={false}
								mixedContentMode='always'
								originWhitelist={['*']}
								// The pack is a file:// document that calls the http(s) API, same flags
								// as the other game WebViews. iOS read grant is scoped to the arcade folder.
								allowFileAccess
								allowFileAccessFromFileURLs
								allowUniversalAccessFromFileURLs
								allowingReadAccessToURL={pack.readAccessUrl}
								injectedJavaScriptBeforeContentLoaded={ARCADE_NATIVE_PRELUDE}
								onMessage={onWebViewMessage}
								onError={syntheticEvent => {
									if (__DEV__) console.error('[arcade] WebView error', syntheticEvent.nativeEvent);
								}}
							/>
						) : (
							<iframe
								ref={iframeRef}
								src={gameUrl}
								style={webIframeStyle}
								title={gameType}
								allow='fullscreen; autoplay'
							/>
						)}
						<ArcadeLoadingOverlay gameType={gameType} done={shown} />
					</View>
				</View>
			</Modal>
			{showBecomeVip && <BecomeVipModal onClose={() => setShowBecomeVip(false)} />}
			<NotEnoughChipsModal
				visible={showNotEnoughChips}
				onClose={() => setShowNotEnoughChips(false)}
				onBuy={onBuyChips}
			/>
		</>
	);
}

const styles = StyleSheet.create({
	overlay: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: 'rgba(0,0,0,0.85)',
	},
	container: {
		width: '100%',
		height: '100%',
		// The games' own black, so the safe-area frame reads as part of the scene.
		backgroundColor: '#050308',
		overflow: 'hidden',
	},
	webview: {
		flex: 1,
		backgroundColor: 'transparent',
	},
	errorContainer: {
		backgroundColor: '#2a0d14',
		padding: 24,
		borderRadius: 16,
		alignItems: 'center',
	},
	errorText: {
		color: '#fff3dc',
		fontSize: 16,
		marginBottom: 16,
		textAlign: 'center',
	},
	errorButton: {
		backgroundColor: '#f8d955',
		paddingHorizontal: 24,
		paddingVertical: 10,
		borderRadius: 8,
	},
	errorButtonText: {
		color: '#12060a',
		fontWeight: '700',
	},
});
